import React, { useState } from 'react';
import {
    User, Briefcase, Target, Brain, CheckSquare,
    Award, Star, MessageSquare, X, MapPin,
    ShieldAlert, MoreVertical, FileText, Trash2,
    ThumbsDown, LogOut, Check, Info, AlertTriangle, Users
} from 'lucide-react';

/**
 * =================================================================================
 * 1. CONFIGURATION & THEME (From Code A - UI Source)
 * =================================================================================
 */
const THEME = {
    colors: {
        primary: '#0d6efd',
        text: '#212529',
        muted: '#64748b',
        border: '#e2e8f0',
        bg: '#f5f8ff',
        success: { text: '#198754', bg: '#d1e7dd', light: '#f0fdf4' },
        warning: { text: '#b45309', bg: '#fff3cd', light: '#fffbeb' }, // Adjusted text color for better contrast
        danger: { text: '#dc3545', bg: '#f8d7da', light: '#fef2f2' },
        info: { text: '#055160', bg: '#eef2ff', light: '#e0f2fe' },
    }
};

const getColor = (index) => {

    if (index === 0) return "rgb(25, 135, 84)"
    if (index === 1) return "rgb(133, 100, 4)"
    if (index === 2) return "rgb(220, 53, 69)"

}

const getText = (index) => {
    if (index === 0) return "Self-Image (Primary)"
    if (index === 1) return "Overall (Synthesis)"
    if (index === 2) return "Natural (Secondary)"
}

/**
 * =================================================================================
 * 2. DATA (From Code B - Logic/API Source)
 * =================================================================================
 */
const DEFAULT_DATA = {
    candidate: {
        name: "Mamtha",
        role: "Motivator & Influencer",
        date: "Dec 2025",
        location: "Bangalore", // Added to fit UI slot
        phone: "8296222980",   // Added to fit UI slot
        email: "mamtha@example.com" // Added to fit UI slot
    },
    executive: {
        bestRole: "Environments that encourage innovation, recognize individual contributions, and value strategic risk-taking. She thrives in settings where she can leverage her competitive drive.",
        managementKey: "Encourage her competitive spirit and foster an environment that values strategic thinking. Support her need for stability while allowing opportunities to showcase strengths.",
        keyRisks: "Potential resistance to change or new ideas due to rigidity. May become possessive in collaborative settings or challenge authority if structures are too rigid.",
        analysis: [
            { title: "The Overall Personality", text: "Mamtha's personality is a fascinating amalgamation of competitiveness, pleasantness, deliberation, and defiance. Her competitive spirit is a driving force that propels her to strive for excellence and achieve her goals. This trait is balanced by her pleasant nature, which enables her to build and maintain harmonious relationships with colleagues and peers." },
            { title: "Self Image", text: "Mamtha's self-image is shaped by her perception of herself as a calculated risk-taker, self-promoting, stable, and rigid individual. This self-perception influences how she presents herself to the world and interacts with others in a work environment." }
        ]
    },
    personalityGroups: {
        primary: { title: "The Motivator", desc: "Dependable, social, assertive, and determined. Have a clear idea of life goals and the will power to achieve them. Strong sense of independence.", tags: ["Dependable", "Socially Skilled"] },
        intermediate: { title: "The Achiever", desc: "Goal-oriented and efficiency-driven. Balances the social nature of an Influencer with the determination of a Motivator to deliver tangible results.", tags: ["Goal-Oriented", "Efficient"] },
        secondary: { title: "The Influencer", desc: "Confident, outgoing, and friendly individuals who love to be the center of attention. Strong communicators who are assertive, intuitive, and adaptive.", tags: ["Persuasive", "Networker"] },
        overall: "Competitive, pleasant, deliberate, and defiant.",
        selfImage: "Calculated risk-taker, self-promoting, stable, rigid.",
        natural: "Adventuresome, charming, possessive, obstinate."
    },
    strengthsAndWeaknesses: {
        strengths: ["Competitive drive and strategic thinking", "Charismatic and pleasant demeanor", "Ability to build positive relationships", "Willingness to take calculated risks"],
        weaknesses: [
            { title: "RIGIDITY", desc: "Potential resistance to change or new ideas." },
            { title: "POSSESSIVENESS", desc: "May become overly protective of tasks/territory." },
            { title: "RECOGNITION", desc: "Strong requirement for external validation." }
        ]
    },
    teamDynamics: {
        conflict: "Defiant nature may lead to questioning authority. Possessiveness and obstinacy can create friction in collaboration.",
        communication: "Charming, persuasive, and articulate. Her natural charisma enhances her ability to influence and inspire others, making her an effective leader in social situations.",
        idealEnvironment: "Innovation-focused, recognizing individual contributions, and valuing strategic outcomes."
    },
    jobMapping: {
        requirements: [
            { name: "ACHIEVER", primary: { rating: "GOOD", text: "Reliable and deliberate. Focuses on controlling circumstances to ensure goals are met without rushing." }, bridging: { rating: "EXCELLENT", text: "Highly competitive and calculating. Strives for excellence and carefully weighs risks to ensure she wins." }, secondary: { rating: "NORMAL", text: "Becomes stubborn ('obstinate'). She will work hard to defend her position but may resist necessary compromises." } },
            { name: "MOTIVATOR", primary: { rating: "EXCELLENT", text: "Warm, patient, and a natural facilitator. She builds good relations and keeps the team atmosphere supportive." }, bridging: { rating: "GOOD", text: "Leads by example through confidence. She is 'self-promoting,' showing others what success looks like." }, secondary: { rating: "EXCELLENT", text: "High energy and charisma. She becomes the 'center of attention' and inspires others through enthusiasm." } },
            { name: "PERSUADER", primary: { rating: "GOOD", text: "Social and dependable. She confidently suggests new ideas and uses her skills to assign tasks effectively." }, bridging: { rating: "GOOD", text: "Challenges the status quo. She has the courage ('defiance') to stand up for her beliefs and advocate for herself." }, secondary: { rating: "EXCELLENT", text: "A natural 'Influencer.' She uses intuition and charm to network, connect easily, and win people over." } }
        ]
    },
    managementStrategy: {
        dos: ["Encourage competitive spirit by setting challenging goals.", "Foster an environment that values strategic thinking.", "Provide opportunities to showcase strengths.", "Support need for stability with clear communication."],
        donts: ["Avoid imposing rigid structures that stifle adventurous nature.", "Refrain from dismissing her ideas or contributions.", "Do not overlook her need for recognition and validation.", "Avoid environments that demand strict conformity."]
    }
};

/**
 * =================================================================================
 * 3. STYLES (From Code A - UI Source)
 * =================================================================================
 */
const CSS_STYLES = `
  :root {
    --lywo-font-base: 14px;
    --lywo-radius: 8px;
  }
  
  body { font-family: 'Inter', sans-serif !important; font-size: var(--lywo-font-base); color: ${THEME.colors.text}; background-color: #f0f2f5; margin: 0; }
  .mainWrap { background-color: ${THEME.colors.bg}; min-height: 100vh }

  /* --- ATOMS (Core UI Elements) --- */
  .card-box { background: #fff; border-radius: var(--lywo-radius); border: 1px solid ${THEME.colors.border}; padding: 1.25rem; box-shadow: 0 1px 3px rgba(0,0,0,0.05); height: 100%; transition: transform 0.2s ease-in-out; }
  
  .section-title { 
    font-size: 0.8rem; font-weight: 700; color: ${THEME.colors.muted}; 
    text-transform: uppercase; margin-bottom: 1rem; 
    display: flex; align-items: center; gap: 0.5rem; letter-spacing: 0.5px; 
  }
  
  .icon-box { width: 32px; height: 32px; border-radius: 6px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .icon-box.blue { background: ${THEME.colors.info.bg}; color: ${THEME.colors.primary}; }
  .icon-box.green { background: ${THEME.colors.success.bg}; color: ${THEME.colors.success.text}; }
  .icon-box.purple { background: #faf5ff; color: #9333ea; }
  .icon-box.red { background: ${THEME.colors.danger.bg}; color: ${THEME.colors.danger.text}; }
  .icon-box.orange { background: ${THEME.colors.warning.bg}; color: ${THEME.colors.warning.text}; }
  .icon-box.gray { background: #f1f5f9; color: ${THEME.colors.muted}; }

  /* Badge Styles */
  .badge-std { display: inline-block; padding: 0.25rem 0.5rem; border-radius: 4px; font-size: 0.7rem; font-weight: 700; text-transform: uppercase; }
  .badge-excellent { background: ${THEME.colors.success.text}; color: #fff; }
  .badge-good { background: ${THEME.colors.success.bg}; color: ${THEME.colors.success.text}; }
  .badge-poor { background: ${THEME.colors.danger.bg}; color: ${THEME.colors.danger.text}; }
  .badge-normal { background: #e2e3e5; color: #475569; }

  /* --- COMPONENT STYLES --- */
  
  /* Header */
  .app-header { background: #fff; border-bottom: 1px solid ${THEME.colors.border}; padding: 1rem 1.5rem; display: flex; justify-content: space-between; align-items: center; position: sticky; top: 0; z-index: 100; box-shadow: 0 1px 2px rgba(0,0,0,0.05); margin-bottom: 1.5rem; }
  .header-title { font-size: 1.4rem; font-weight: 700; margin: 0; line-height: 1.2; }
  .header-meta { font-size: 0.85rem; color: ${THEME.colors.muted}; margin: 0; }
  
  /* Lists */
  .list-item { border-bottom: 1px solid ${THEME.colors.border}; padding: 0.75rem 0; font-size: 0.9rem; line-height: 1.5; color: #4b5563; }
  .list-item:last-child { border: none; }

  /* Tables */
  .custom-table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 0.85rem; }
  .custom-table th { background: #f8fafc; color: ${THEME.colors.muted}; font-weight: 600; text-transform: uppercase; padding: 0.75rem; text-align: left; border-bottom: 2px solid ${THEME.colors.border}; }
  .custom-table td { padding: 0.75rem; border-bottom: 1px solid ${THEME.colors.border}; vertical-align: top; }
  .custom-table tr:last-child td { border-bottom: none; }

  /* Analysis Text */
  .analysis-text { font-size: 0.95rem; line-height: 1.6; color: #334155; text-align: justify; }

  /* Context Menu */
  .context-menu-btn { background: none; border: none; padding: 4px; border-radius: 4px; cursor: pointer; color: ${THEME.colors.muted}; display: flex; }
  .context-menu-btn:hover { background: #f1f5f9; color: ${THEME.colors.primary}; }

  @media print {
    .no-print { display: none !important; }
    .mainWrap { padding: 0; background: white; }
    .card-box { box-shadow: none; border: 1px solid #ccc; break-inside: avoid; }
  }
`;



const Card = ({ children, className = "", style = {} }) => (
    <div className={`card-box ${className}`} style={style}>{children}</div>
);

const SectionTitle = ({ icon: Icon, title }) => (
    <div className="section-title">
        {Icon && <Icon size={16} />} {title}
    </div>
);



const Badge = ({ text }) => {
    const type = text?.toUpperCase() === 'EXCELLENT' ? 'excellent'
        : text.toUpperCase() === 'GOOD' ? 'good'
            : text.toUpperCase() === 'POOR' ? 'poor'
                : 'normal';
    return <span className={`badge-std badge-${type}`}>{text}</span>;
};





const PersonalitySection = ({ data, handleShowPersonality }) => {
    return (
        <Card>
            <SectionTitle icon={User} title="Personality Groups" />
            <div className="row g-3">
                {data?.map((group, index) => (
                    <div className="col-md-4">
                        <div className="h-100 p-3 rounded" style={{ border: `1px solid ${THEME.colors.border}`, borderLeft: `4px solid ${getColor(index)}` }}>
                            <strong className="text-muted small text-uppercase d-block mb-1" style={{ fontSize: ' 0.75rem' }}>{getText(index)}</strong>
                            <h4 style={{ color: getColor(index), fontSize: '1.1rem', fontWeight: '700', marginBottom: '0.5rem' }}
                                onClick={() => handleShowPersonality(group?.personality, true)}
                            >
                                The {group?.personality}
                            </h4>
                            <p className="mb-2 small text-muted">{group?.description}</p>
                            {/* <div className="d-flex flex-wrap gap-1">
                                {data[group.key].tags.map(tag => (
                                    <span key={tag} style={{ fontSize: '0.7rem', padding: '2px 6px', background: '#f1f5f9', borderRadius: '4px', border: '1px solid #e2e8f0', fontWeight: 600 }}>{tag}</span>
                                ))}
                            </div> */}
                        </div>
                    </div>
                ))}
            </div>
        </Card>
    );
};

// 5.4 Job Role Mapping (CRITICAL MERGE: Data B -> Layout A)
const JobMappingSection = ({ mapping, groups, match, handleShowPersonality }) => (
    <Card>
        <div className="d-flex justify-content-between align-items-center mb-2">
            <SectionTitle icon={Target} title="Job Role Mapping" />
            <div style={{ marginTop: '-20px' }}>
                <span className="badge match " >{match}</span>
            </div>
        </div>

        <div className="table-responsive">
            <table className="custom-table">
                <thead>
                    <tr>
                        <th style={{ width: '20%' }}>Requirement</th>
                        <th style={{ width: '26%' }}>Primary<br /><span className="small fw-normal text-muted" onClick={() => handleShowPersonality(mapping[0]?.trait, true)}>{mapping[0]?.trait}</span></th>
                        <th style={{ width: '26%', background: THEME.colors.warning.light }}>Overall<br /><span className="small fw-normal text-muted" onClick={() => handleShowPersonality(mapping[1]?.trait, true)}>{mapping[1]?.trait}</span></th>
                        <th style={{ width: '26%', background: THEME.colors.danger.light }}>Secondary<br /><span className="small fw-normal text-muted" onClick={() => handleShowPersonality(mapping[2]?.trait, true)}>{mapping[2]?.trait}</span></th>
                    </tr>
                </thead>
                <tbody>
                    {mapping?.map((req, i) => (
                        <tr key={i}>
                            <td className="fw-bold bg-light align-middle" onClick={() => handleShowPersonality(req?.trait, true)}>{req?.trait}</td>
                            <td>
                                <Badge text={req?.primary?.rating || ""} /><br />
                                <div className="mt-2 text-muted small">{req?.primary?.justification}</div>
                            </td>
                            <td style={{ background: THEME.colors.warning.light }}>
                                <Badge text={req?.analysis?.rating || ""} /><br />
                                <div className="mt-2 text-muted small">{req?.analysis?.justification}</div>
                            </td>
                            <td style={{ background: THEME.colors.danger.light }}>
                                <Badge text={req?.secondary?.rating || ""} /><br />
                                <div className="mt-2 text-muted small">{req?.secondary?.justification}</div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </Card>
);









const UserData = ({ data = DEFAULT_DATA, roleMapping, match, personalities, handleShowPersonality }) => {
    return (
        <div className="">
            {/* Injects CSS & Bootstrap */}
            <style>
                {`@import url('https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css');`}
                {CSS_STYLES}
            </style>


            <div className="">
                {/* Row 1: High Level Strategy & Executive Summary */}


                {/* Row 2: Deep Dive (Personality & Job Mapping) */}
                <div className="row g-3 ">
                    <div className="col-12">
                        <PersonalitySection data={personalities} handleShowPersonality={handleShowPersonality} />
                    </div>
                    <div className="col-12">
                        <JobMappingSection mapping={roleMapping || []} groups={data.personalityGroups} match={match}
                            handleShowPersonality={handleShowPersonality}
                        />
                    </div>
                </div>

                {/* Row 3: Team & Strengths */}


            </div>


        </div>
    );
};

export default UserData;

